import { useBrandName } from '../brand'; /* platform_brand_v1 */
// mobile_v1: เพิ่ม Bottom Navigation บนมือถือ + ปรับปุ่ม hamburger
// คง desktop UI เดิมทั้งหมด — เปลี่ยนเฉพาะ mobile experience
import React, { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { MessageSquare, LayoutDashboard, Radio, Users, BarChart2, Settings, LogOut, Zap, Bot, Send, Gamepad2, ChevronDown, ChevronRight, Clock, Plus, User, X, Calendar, HelpCircle, Gift, FileText, Package, Megaphone, Moon, Sun, Sparkles } from 'lucide-react'; // invoice_frontend_v1
import API from '../api';
import { connectSSE } from '../lib/sse'; /* persistentAlert_v2: SSE สำหรับเสียงเตือนทันที */
/* sound_single_source_v1: ใช้ค่าตั้งเสียงชุดเดียวกับ Inbox (notify_sound_v1) — ปุ่ม 🔕 ต้องปิดได้ทุกที่ */
import { playNotifySound, getSoundSettings, unlockAudio as unlockNotifyAudio } from '../lib/notifySound';
import { fetchConversations, resetConversationsCache } from '../lib/conversations'; /* conv_share_v1 */
import { useAdaptivePoll } from '../lib/useAdaptivePoll'; /* poll_adaptive_v1 */
import StaffChatWidget from './StaffChatWidget'; /* staff_chat_v1 widget import */
import SubscriptionWatch from './SubscriptionWatch'; /* expiry_v1 */
import SuspendedScreen from './SuspendedScreen'; /* tenant_suspended_modal_v1 */
import ClockButton from './ClockButton'; // shifts-v1
import ShiftReminderBanner from './ShiftReminderBanner'; // shifts-v1
// shifts-v1: pending shift state shared between Banner ↔ ClockButton
import TokenHealthBanner from './TokenHealthBanner'; // token-alert-v1

const TYPE_ICONS = { line: '💚', telegram: '✈️', facebook: '💙', whatsapp: '💬', webchat: '🌐' };

const nav = [
  { to: '/channels', icon: Radio, label: 'Channels' },
  { to: '/agents', icon: Users, label: 'Agents' },
  { to: '/quick-replies', icon: Zap, label: 'Quick Replies' },
  { to: '/broadcast', icon: Megaphone, label: 'บรอดแคสต์' }, // broadcast_v1
  { to: '/team', icon: Users, label: 'พนักงาน' }, // team_status_v1
  { to: '/kpi', icon: BarChart2, label: 'KPI' },
  { to: '/shifts', icon: Calendar, label: 'ตารางเวร' }, // shifts-v1
  { to: '/bot-training', icon: Bot, label: 'เทรนบอท' },
  { to: '/ai', icon: Sparkles, label: 'ผู้ช่วย AI' }, // ai_ui_v1
  { to: '/telegram-settings', icon: Send, label: 'Telegram' },
  { to: '/hot-games', icon: Gamepad2, label: 'เกม' },
  { to: '/invoices', icon: FileText, label: 'บิล' }, // invoice_frontend_v1
  { to: '/products', icon: Package, label: 'สินค้า' }, // invoice_frontend_v1
  { to: '/customers', icon: Users, label: 'ลูกค้า' }, // customer_list_v1
  { to: '/live-selling', icon: Radio, label: 'ไลฟ์ขาย (CF)' },
  { to: '/settings', icon: Settings, label: 'Settings' },
  { to: '/help', icon: HelpCircle, label: 'ช่วยเหลือ' }, // manuals_v1
  { to: '/invite', icon: Gift, label: 'ชวนเพื่อน' }, // referral_menu_v1
];

export default function Layout() {
  const _brand = useBrandName(); /* platform_brand_v1 */
  const navigate = useNavigate();
  const logout = async () => {
    // logout_v2: clock_out + presence cleanup
    try { await API.post('/api/time/clock-out'); } catch (e) {}
    try { await API.delete('/api/online/logout'); } catch (e) {}
    // single_session_ui_v1: clear server-side active_token_jti
    try { await API.post('/api/auth/logout'); } catch (e) {}
    resetConversationsCache(); /* conv_share_v1: กันคนที่ login ต่อเห็นรายการแชทของคนก่อน */
    localStorage.clear(); navigate('/login'); };
  const tenant = JSON.parse(localStorage.getItem('tenant') || '{}');
  const [inboxOpen, setInboxOpen] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // mobile_v1: state สำหรับ bottom nav drawers
  const [moreOpen, setMoreOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [staffUnread, setStaffUnread] = useState(0); // staff_chat_sidebar_v1
  const [dark, setDark] = useState(() => { try { return document.documentElement.getAttribute('data-theme') === 'dark'; } catch (e) { return false; } }); // theme_dark_v1

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // staff_chat_sidebar_v1: receive unread count from StaffChatWidget
  useEffect(() => {
    const onUnread = (e) => setStaffUnread(Number(e.detail) || 0);
    window.addEventListener('staff-chat-unread', onUnread);
    return () => window.removeEventListener('staff-chat-unread', onUnread);
  }, []);

  // mobile_v2: dynamic viewport height — แก้ iOS Safari address bar ทำให้ 100vh ใหญ่กว่า viewport จริง
  useEffect(() => {
    const setRealVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--app-vh', `${vh}px`);
    };
    setRealVh();
    window.addEventListener('resize', setRealVh);
    window.addEventListener('orientationchange', setRealVh);
    return () => {
      window.removeEventListener('resize', setRealVh);
      window.removeEventListener('orientationchange', setRealVh);
    };
  }, []);
  const isAgent = tenant.role === 'agent' || tenant.role === 'staff' || tenant.role === 'admin' || tenant.role === 'viewer';
  const perms = tenant.permissions || {};
  // perms_v2: full coverage
  const canSee = (key) => {
    if (!isAgent) return true; // tenant เห็นทุกอย่าง
    if (key === 'inbox') return perms.inbox !== false;
    if (key === 'kpi') return perms.view_kpi !== false;
    if (key === 'time_report') return true; // hybrid: ทุก agent เห็นเมนู — backend filter ตัวเอง/ทีม
    if (key === 'kb') return perms.manage_kb !== false;
    if (key === 'channels') return perms.manage_channels !== false;
    if (key === 'quickreplies') return perms.manage_quick_replies !== false;
    /* mobile_broadcast_fix_v1: ต้องได้สิทธิ์มาก่อนถึงเห็น (ไม่ใช่เห็นก่อนแล้วค่อยห้าม)
       ให้ตรงกับกติกาฝั่งเดสก์ท็อป: (!isAgent || perms.broadcast === true) */
    if (key === 'broadcast') return perms.broadcast === true;
    if (key === 'agents') return perms.manage_agents === true;
    if (key === 'telegram') return perms.manage_telegram === true;
    if (key === 'hot_games') return perms.manage_hot_games === true;
    if (key === 'ai_assistant') return perms.ai_assistant === true;
    if (key === 'settings') return perms.settings !== false;
    return true;
  };
  // bill_sso_v1: seamless jump to the separate bill app
  const openBillSystem = async () => {
    try {
      const r = await API.post('/api/auth/bill-handoff');
      const handoff = r.data && r.data.handoff;
      const h = (typeof window !== 'undefined' && window.location.hostname) || '';
      const billHost = /(^|\.)autodplus\.vip$/.test(h) ? 'bill.autodplus.vip' : 'bill.aiautochat.net';
      const url = 'https://' + billHost + '/#sso=' + encodeURIComponent(handoff || '');
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      const msg = (e && e.response && e.response.data && e.response.data.error) || 'เปิดระบบบิลไม่สำเร็จ'; /* bill_sso_perm_v1 */
      try { window.alert(msg); } catch (e2) {}
    }
  };
  // bill_trial_v1: self-start a free 3-day bill trial, then jump straight in
  const startBillTrial = async () => {
    if (!window.confirm('เริ่มทดลองใช้ระบบบิลฟรี 3 วัน?')) return;
    try {
      await API.post('/api/tenants/bill-trial');
      try { localStorage.removeItem('tenant_features'); } catch (e) {}
      setFeatures(function (f) { return { ...f, hasBill: true, billAccess: true, billTrialAvailable: false }; });
      window.alert('เริ่มทดลองใช้ระบบบิลฟรี 3 วันแล้ว! กำลังเปิดระบบบิล…');
      await openBillSystem();
    } catch (e) {
      const msg = (e && e.response && e.response.data && e.response.data.error) || 'เริ่มทดลองไม่สำเร็จ';
      try { window.alert(msg); } catch (e2) {}
    }
  };
  const [channels, setChannels] = useState([]);
  // feature_flags_v1: tenant-level feature gates
  const [features, setFeatures] = useState(() => {
    try {
      const cached = localStorage.getItem('tenant_features');
      if (cached) return JSON.parse(cached);
    } catch (e) {}
    return { hasChat: true, hasBill: true, billAccess: false, billTrialAvailable: false }; // safe default (bill_sso_perm_v1 + bill_trial_v1)
  });
  useEffect(() => {
    API.get('/api/tenants/me').then(r => {
      const t = r.data && r.data.tenant ? r.data.tenant : {};
      const f = {
        hasChat: t.has_chat_feature !== false,
        hasBill: t.has_bill_feature !== false,
        hasCF: t.has_cf_feature === true,
        billAccess: r.data && r.data.bill_access === true, /* bill_sso_perm_v1 */
        billTrialAvailable: r.data && r.data.bill_trial_available === true, /* bill_trial_v1 */
      };
      setFeatures(f);
      try { localStorage.setItem('tenant_features', JSON.stringify(f)); } catch (e) {}
    }).catch(() => {});
  }, []);
  const [convCounts, setConvCounts] = useState({});

  const prevCountRef = useRef(null);

  const audioCtxRef = useRef(null);

  /* sound_single_source_v1: อยู่หน้า /inbox หรือเปล่า — ถ้าอยู่ ปล่อยให้ Inbox เล่นเสียงข้อความใหม่คนเดียว
     (ใช้ ref เพราะ loadChannelCounts/SSE handler ผูก closure ตั้งแต่ render แรก) */
  const isInboxRouteRef = useRef(false);

  /* badge_count_fix_v1: id ของเคสที่ "เปิดอยู่" และถูกนับใน _total ไปแล้ว
     ใช้ตัดสินว่าข้อความใหม่ที่เข้ามาเป็นเคสใหม่ (ต้องบวก) หรือเคสเดิม (ไม่ต้องบวก) */
  const openConvIdsRef = useRef(new Set());

  /* persistentAlert_v1: ดังซ้ำทุก 5 วินาที จนกว่าทุกเคสจะถูกรับ */
  const pendingCasesRef = useRef(new Set());      // case ids ที่ยังไม่รับ
  const alertIntervalRef = useRef(null);          // interval id ที่กำลังวน
  const audioUnlockedRef = useRef(false);         // ตรวจว่า user ได้ click หน้าจอแล้วหรือยัง

  const stopAlerting = () => {
    if (alertIntervalRef.current) {
      clearInterval(alertIntervalRef.current);
      alertIntervalRef.current = null;
    }
    pendingCasesRef.current.clear();
  };

  const startAlertingIfNeeded = () => {
    if (pendingCasesRef.current.size === 0) {
      stopAlerting();
      return;
    }
    if (alertIntervalRef.current) return; // already alerting
    /* persistentAlert_v2: ดังทันทีก่อน setInterval (ไม่ต้องรอ 5 วิแรก) */
    playNotification();
    alertIntervalRef.current = setInterval(() => {
      if (pendingCasesRef.current.size === 0) {
        stopAlerting();
        return;
      }
      playNotification();
    }, 5000);
  };

  const unlockAudio = () => {
    /* sound_single_source_v1: ปลดล็อก AudioContext ของ notifySound ด้วย
       ไม่งั้นเสียงข้อความใหม่นอกหน้า /inbox จะเงียบ (context ยัง suspended) */
    try { unlockNotifyAudio(); } catch (e) {}
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (audioCtxRef.current.state === 'suspended') {
        audioCtxRef.current.resume().then(() => {
          audioUnlockedRef.current = true;
        }).catch(() => {});
      } else {
        audioUnlockedRef.current = true;
      }
    } catch {}
  };

  /* sound_single_source_v1: เสียงเตือน "เคสค้างยังไม่มีคนรับ" (ดังซ้ำทุก 5 วิ)
     — เคารพสวิตช์เปิด/ปิด + ความดัง ที่ผู้ใช้ตั้งไว้ในกล่องข้อความ */
  const playNotification = () => {
    try {
      const s = getSoundSettings();
      if (!s.enabled) return;
      const vol = 0.8 * s.volume;
      if (vol <= 0) return;
      /* persistentAlert_v1: เล่นเฉพาะหลัง user click (browser block ก่อนหน้านั้น) */
      if (!audioUnlockedRef.current) return;
      if (!audioCtxRef.current) return;
      const ctx = audioCtxRef.current;
      if (ctx.state !== 'running') return; // ถ้า suspended อย่าพยายามเล่น
      /* persistentAlert_v2: 2 beeps ติดกัน + volume สูง + sawtooth wave (โดดเด่นกว่า sine) */
      const playBeep = (startOffset, freqStart, freqEnd) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sawtooth'; // เสียงคมขึ้น โดดเด่น (vs sine ที่นุ่ม)
        osc.connect(gain);
        gain.connect(ctx.destination);
        const t0 = ctx.currentTime + startOffset;
        osc.frequency.setValueAtTime(freqStart, t0);
        osc.frequency.linearRampToValueAtTime(freqEnd, t0 + 0.15);
        gain.gain.setValueAtTime(0, t0);
        gain.gain.linearRampToValueAtTime(vol, t0 + 0.02); // attack เร็ว
        gain.gain.setValueAtTime(vol, t0 + 0.13);          // hold ที่ volume สูง
        gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.25); // release
        osc.start(t0);
        osc.stop(t0 + 0.25);
      };
      // beep 1: 900Hz → 1200Hz
      playBeep(0, 900, 1200);
      // beep 2: 1200Hz → 900Hz (ใจสองพยางค์ ดิ๊ง-ด๊อง)
      playBeep(0.3, 1200, 900);
    } catch(e) {}
  };

  /* sound_single_source_v1: เสียง "ข้อความใหม่" นอกหน้ากล่องข้อความ
     ใช้ preset/ความดังชุดเดียวกับที่ผู้ใช้เลือกไว้ใน Inbox — จะได้ยินเสียงเดียวกันทุกหน้า */
  const playNewMessageSound = () => {
    try {
      const s = getSoundSettings();
      if (!s.enabled) return;
      playNotifySound(s.sound, s.volume);
    } catch (e) {}
  };

  const PAGE_LABELS = {
    '/': 'Dashboard', '/inbox': 'Inbox', '/channels': 'Channels',
    '/agents': 'Agents', '/kpi': 'KPI', '/bot-training': 'เทรนบอท', '/ai': 'ผู้ช่วย AI',
    '/settings': 'Settings', '/quick-replies': 'Quick Replies',
    '/telegram-settings': 'Telegram', '/hot-games': 'เกม', '/invoices': 'บิล', '/products': 'สินค้า', '/customers': 'ลูกค้า' /* invoice_frontend_v1 + customer_list_v1 */
  };

  const [onlineUsers, setOnlineUsers] = useState([]);
  const location = useLocation();
  /* navlinkActiveFix_v2 */
  const currentChannel = new URLSearchParams(location.search).get('channel');
  const isInboxAll = location.pathname === '/inbox' && !currentChannel;
  isInboxRouteRef.current = location.pathname.startsWith('/inbox'); /* sound_single_source_v1 */

  const sendHeartbeat = async () => {
    try {
      const page = PAGE_LABELS[location.pathname] || location.pathname;
      await API.post('/api/online/heartbeat', { current_page: page });
      API.post('/api/time/heartbeat').catch(() => {}); // time_v1
    } catch {}
  };

  const loadOnlineUsers = async () => {
    try {
      const { data } = await API.get('/api/online');
      setOnlineUsers(data);
    } catch {}
  };

  /* persistentAlert_v1: register unlock handlers แค่ครั้งเดียวตอน mount
     ใช้ click/touchstart ทุกครั้ง (ไม่ใช่ once) เพื่อ resume context หาก suspended อีก */
  useEffect(() => {
    document.addEventListener('touchstart', unlockAudio);
    document.addEventListener('click', unlockAudio);
    return () => {
      document.removeEventListener('touchstart', unlockAudio);
      document.removeEventListener('click', unlockAudio);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    sendHeartbeat();
    loadOnlineUsers();
    /* jitter_v1: spread heartbeat/online polls 0-5s so all agents do not hit DB at the same instant (thundering herd → pool burst) */
    const heartbeatInterval = setInterval(() => setTimeout(sendHeartbeat, Math.random() * 5000), 30000);
    const onlineInterval = setInterval(() => setTimeout(loadOnlineUsers, Math.random() * 5000), 15000);
    return () => {
      clearInterval(heartbeatInterval);
      clearInterval(onlineInterval);
      // logout_v2: ไม่ clock_out ตอน unmount/navigate — ให้ cron จัดการ (30 นาที timeout)
      // หรือ trigger จาก beforeunload (ปิด tab) / logout button เท่านั้น
    };
  }, [location.pathname]);

  // logout_v2: handle ปิด tab/browser → sendBeacon clock_out
  useEffect(() => {
    /* jwt_no_url_v1: เดิมใช้ sendBeacon ซึ่งใส่ header ไม่ได้ เลยต้องแปะ JWT ไว้ใน query string
       → token เต็มๆ ไปโผล่ใน access log ของ nginx ทุกครั้งที่มีคนปิดแท็บ
       fetch + keepalive ส่งต่อได้หลังปิดแท็บเหมือน sendBeacon แต่ใส่ Authorization header ได้
       สอง endpoint นี้รับ header แน่นอน เพราะ logout() ด้านบนเรียกผ่าน axios ที่แนบ header ให้อยู่แล้ว */
    const handleUnload = () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      let canKeepalive = false;
      try { canKeepalive = 'keepalive' in new Request('/'); } catch (e) { canKeepalive = false; }

      if (canKeepalive) {
        const auth = { Authorization: `Bearer ${token}` };
        try {
          fetch('/api/time/clock-out', {
            method: 'POST', keepalive: true,
            headers: { ...auth, 'Content-Type': 'application/json' }, body: '{}',
          }).catch(() => {});
          fetch('/api/online/logout', { method: 'POST', keepalive: true, headers: auth }).catch(() => {});
          return;
        } catch (e) { /* ตกไปใช้ทางเดิมด้านล่าง */ }
      }

      /* เบราว์เซอร์เก่าที่ยังไม่รองรับ keepalive — ยอมใช้ของเดิมไปก่อน ดีกว่าลงเวลาออกไม่สำเร็จ
         (ถึงพลาดก็ยังมี cron ฝั่ง server ปิดกะให้เมื่อไม่มีสัญญาณ 30 นาที) */
      const blob = new Blob([JSON.stringify({})], { type: 'application/json' });
      navigator.sendBeacon('/api/time/clock-out?token=' + encodeURIComponent(token), blob);
      navigator.sendBeacon('/api/online/logout?token=' + encodeURIComponent(token));
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  /* conv_share_v1: opts.minTime ส่งต่อไปให้ fetchConversations
     — SSE ส่งมา = เวลาที่ event มาถึง, หลังรับ/ปิดเคส = เวลาหลัง API ตอบ */
  const loadChannelCounts = async (opts) => {
    try {
      const ts = Date.now();
      const [chRes, convData] = await Promise.all([
        API.get(`/api/channels?_=${ts}`),
        fetchConversations(opts) /* conv_share_v1: เกาะ request เดียวกับ Inbox ถ้ายิงพร้อมกัน */
      ]);
      setChannels(chRes.data);
      // นับ open conversations ต่อ channel
      const counts = {};
      let total = 0;
      let latestMsgTime = 0;
      /* persistentAlert_v1: track เคสที่ยังไม่ถูกรับ */
      const newPending = new Set();
      const newOpenIds = new Set(); /* badge_count_fix_v1 */
      convData.forEach(c => {
        if (c.status === 'open') {
          counts[c.channel_id] = (counts[c.channel_id] || 0) + 1;
          total++;
          newOpenIds.add(String(c.id)); /* badge_count_fix_v1 */
          // เคสที่ "ยังไม่รับ" = open + handled_by ไม่ใช่ agent
          if (c.handled_by !== 'agent') {
            newPending.add(c.id);
          }
        }
        if (c.last_message_at) {
          const t = new Date(c.last_message_at).getTime();
          if (t > latestMsgTime) latestMsgTime = t;
        }
      });
      counts['_total'] = total;
      // เสียงเมื่อมีข้อความใหม่ (ดังครั้งแรกทันที)
      /* sound_single_source_v1: อยู่หน้า /inbox แล้ว Inbox เล่นเองอยู่ — ไม่ต้องเล่นซ้ำ */
      if (prevCountRef.current !== null && latestMsgTime > 0 && latestMsgTime > prevCountRef.current && !isInboxRouteRef.current) {
        playNewMessageSound();
      }
      if (latestMsgTime > 0) prevCountRef.current = latestMsgTime;
      openConvIdsRef.current = newOpenIds; /* badge_count_fix_v1: ยึดค่าจากเซิร์ฟเวอร์เป็นหลักทุกรอบ */
      /* persistentAlert_v1: update pending cases + start/stop alert loop */
      pendingCasesRef.current = newPending;
      startAlertingIfNeeded();
      setConvCounts(counts);
    } catch {}
  };

  useEffect(() => {
    loadChannelCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /* poll_adaptive_v1: badge/นับเคส — SSE ต่ออยู่ก็ยืดเป็น 3 นาที (SSE ส่ง conversation_updated มาให้อยู่แล้ว) */
  useAdaptivePoll(() => loadChannelCounts(), { fastMs: 60000, slowMs: 180000 });

  /* persistentAlert_v2: SSE listener — refresh counts ทันทีเมื่อมีข้อความใหม่ (ไม่ต้องรอ poll 10s) */
  const loadChannelCountsRef = useRef(null);
  loadChannelCountsRef.current = loadChannelCounts;
  const lccTimer = useRef(null); /* debounce_v1: coalesce SSE-triggered loadChannelCounts */
  /* conv_share_v1: ส่งเวลาที่ event มาถึงเป็น minTime — Inbox ได้ event เดียวกันในจังหวะเดียวกัน
     ทั้งคู่จึงเกาะ request เดียวกันได้ แทนที่จะยิงคนละเส้นเหมือนเดิม */
  const debouncedLCC = () => {
    const at = Date.now();
    clearTimeout(lccTimer.current);
    lccTimer.current = setTimeout(() => { if (loadChannelCountsRef.current) loadChannelCountsRef.current({ minTime: at }); }, 400);
  };
  useEffect(() => {
    const conn = connectSSE({
      /* poll_adaptive_v1: กลับมาจากช่วง SSE หลุด → ดึงนับใหม่ทันที ไม่ต้องรอรอบ poll
         conv_share_v1: ใช้ info.at (เวลาที่ต่อติด) ให้ตรงกับ Inbox จะได้เกาะ request เดียวกัน */
      onConnect: (info) => { if (loadChannelCountsRef.current) loadChannelCountsRef.current({ minTime: (info && info.at) || Date.now() }); },
      /* sse_instant_badge_v1: bump total + sound instantly on new customer message */
      onNewMessage: (data) => {
        if (data && data.message && data.message.sender_type === 'customer') {
          /* badge_count_fix_v1: _total คือจำนวน "เคสที่เปิดอยู่" ไม่ใช่จำนวนข้อความ
             เดิมบวก 1 ทุกข้อความ ลูกค้ารายเดิมพิมพ์รัว 5 ข้อความ badge ก็บวก 5
             ตอนนี้บวกเฉพาะเคสที่ยังไม่เคยนับ แล้วรอบ refresh ถัดไปจะยึดค่าจากเซิร์ฟเวอร์ทับให้เอง */
          const convId = data.conversationId != null ? String(data.conversationId) : null;
          if (convId && !openConvIdsRef.current.has(convId)) {
            openConvIdsRef.current.add(convId);
            setConvCounts(prev => ({ ...prev, _total: (prev._total || 0) + 1 }));
          }
          /* sound_single_source_v1: หน้า /inbox ให้ Inbox เล่นเสียงคนเดียว — กันเสียงดังซ้อน 2 ชุด */
          if (!isInboxRouteRef.current) playNewMessageSound();
        }
      },
      onConversationUpdated: () => { /* debounce_call_1: coalesce rapid SSE events */
        debouncedLCC();
      }
    });
    return () => conn.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* sse_auth_stop_v1: SSE ยืนยันแล้วว่า token ใช้ไม่ได้ (401/403 จาก endpoint เดิม)
     แปลว่าทุก API call กำลังพังหมด — หน้าจอจะค้างแสดงข้อมูลเก่าไปเรื่อยๆ ถ้าไม่ทำอะไร
     ก่อนหน้านี้ api.js จัดการเฉพาะ SESSION_REPLACED ส่วน token หมดอายุธรรมดาไม่มีใครรับ */
  useEffect(() => {
    let handled = false;
    const onAuthFailed = () => {
      if (handled) return;
      handled = true;
      try { window.alert('เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่'); } catch (e) {}
      try { localStorage.removeItem('token'); localStorage.removeItem('tenant'); } catch (e) {}
      navigate('/login');
    };
    window.addEventListener('sse-auth-failed', onAuthFailed);
    return () => window.removeEventListener('sse-auth-failed', onAuthFailed);
  }, [navigate]);

  /* persistentAlert_v1: รับ event 'case-accepted' จาก Inbox → หยุดเสียงทันที (ไม่รอ poll) */
  useEffect(() => {
    const onCaseAccepted = (e) => {
      const id = e?.detail?.id;
      if (id) {
        pendingCasesRef.current.delete(id);
        if (pendingCasesRef.current.size === 0) stopAlerting();
        /* badge_close_sync_v1: refresh counts instantly on close/accept (no 60s poll wait)
           conv_share_v1: event นี้มาหลัง PATCH สำเร็จแล้ว → ต้องได้ข้อมูลที่ดึงหลังจากนั้นเท่านั้น
           (Inbox ก็ขอสดเหมือนกันในจังหวะเดียวกัน ทั้งคู่จึงเกาะ request เดียวกัน) */
        if (loadChannelCountsRef.current) loadChannelCountsRef.current({ minTime: Date.now() });
      }
    };
    window.addEventListener('case-accepted', onCaseAccepted);
    return () => {
      window.removeEventListener('case-accepted', onCaseAccepted);
      stopAlerting(); // cleanup ตอน unmount
    };
  }, []);

  // mobile_v1: ปิด drawer เมื่อเปลี่ยนหน้า
  useEffect(() => {
    setMoreOpen(false);
    setProfileOpen(false);
    setSidebarOpen(false);
  }, [location.pathname, location.search]);

  const navStyle = (isActive) => ({
    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 24px',
    color: isActive ? '#0ea5e9' : '#64748b', textDecoration: 'none',
    background: isActive ? '#f0f9ff' : 'transparent', fontSize: 14,
    borderRight: isActive ? '3px solid #0ea5e9' : '3px solid transparent',
    fontWeight: isActive ? 600 : 400,
  });

  // mobile_v1: ตรวจสอบ active สำหรับ bottom nav
  const isInboxRoute = location.pathname.startsWith('/inbox');
  const isReportRoute = location.pathname === '/kpi' || location.pathname === '/' || location.pathname.startsWith('/reports');

  // mobile_v1: bottom nav button helper
  const BottomNavBtn = ({ icon: Icon, label, active, onClick, badge }) => (
    <button onClick={onClick}
      style={{
        flex: 1, background: 'none', border: 'none', cursor: 'pointer',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: '6px 4px', gap: 2,
        color: active ? '#EF9F27' : '#94a3b8',
        position: 'relative', minHeight: 56,
      }}>
      <div style={{ position: 'relative' }}>
        <Icon size={22} strokeWidth={active ? 2.4 : 2} />
        {badge > 0 && (
          <span style={{
            position: 'absolute', top: -4, right: -8,
            background: '#ef4444', color: '#fff', borderRadius: 10,
            padding: '1px 5px', fontSize: 10, fontWeight: 700, minWidth: 16, textAlign: 'center',
            border: '2px solid #fff', lineHeight: 1.2,
          }}>{badge > 99 ? '99+' : badge}</span>
        )}
      </div>
      <span style={{ fontSize: 11, fontWeight: active ? 600 : 400 }}>{label}</span>
    </button>
  );

  // mobile_v1: เมนูใน drawer "เพิ่มเติม"
  const moreMenuItems = [
    { to: '/', icon: LayoutDashboard, label: 'Dashboard', perm: 'dashboard' },
    { to: '/channels', icon: Radio, label: 'ช่องทาง', perm: 'channels' },
    { to: '/agents', icon: Users, label: 'แอดมิน', perm: 'agents' },
    { to: '/quick-replies', icon: Zap, label: 'ข้อความด่วน', perm: 'quickreplies' },
    /* mobile_broadcast_fix_v1: เมนูนี้มีแต่ในรายการของเดสก์ท็อป (const nav) ตกหล่นจากรายการมือถือ
       ทำให้บนมือถือเข้าหน้าบรอดแคสต์ไม่ได้เลย ไม่ใช่แค่มองไม่เห็น */
    { to: '/broadcast', icon: Megaphone, label: 'บรอดแคสต์', perm: 'broadcast' }, // broadcast_v1
    { to: '/team', icon: Users, label: 'พนักงาน', perm: 'time_report' }, // team_merge_v1
    { to: '/shifts', icon: Calendar, label: 'ตารางเวร', perm: null },
    { to: '/bot-training', icon: Bot, label: 'เทรนบอท', perm: 'kb' },
    { to: '/ai', icon: Sparkles, label: 'ผู้ช่วย AI', perm: 'ai_assistant' }, // ai_ui_v1
    { to: '/telegram-settings', icon: Send, label: 'Telegram', perm: 'telegram' },
    { to: '/hot-games', icon: Gamepad2, label: 'เกม', perm: 'hot_games' },
    { to: '/invoices', icon: FileText, label: 'บิล', perm: 'invoices' }, // invoice_frontend_v1
    { to: '/products', icon: Package, label: 'สินค้า', perm: 'products' }, // invoice_frontend_v1
    { to: '/customers', icon: Users, label: 'ลูกค้า', perm: 'customers' }, // customer_list_v1
  { to: '/live-selling', icon: Radio, label: 'ไลฟ์ขาย (CF)' },
    { to: '/settings', icon: Settings, label: 'ตั้งค่า', perm: 'settings' },
    { to: '/help', icon: HelpCircle, label: 'ช่วยเหลือ', perm: null }, // manuals_v1
    { to: '/invite', icon: Gift, label: 'ชวนเพื่อน', perm: null }, // referral_menu_v1
  ];

  return (
    <>
      <SubscriptionWatch />
      <SuspendedScreen />{/* tenant_suspended_modal_v1 */}
      <TokenHealthBanner />
      <div style={{ display: 'flex', height: isMobile ? 'calc(var(--app-vh, 1vh) * 100)' : '100vh', background: '#f8fafc' }}>
      {/* Mobile overlay (sidebar เก่า — ใช้บน desktop เท่านั้นตอนนี้ จริงๆ แล้วซ่อน sidebar บน mobile) */}
      {sidebarOpen && !isMobile && (
        <div onClick={() => setSidebarOpen(false)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:99 }} />
      )}

      {/* Mobile hamburger — ซ่อนแล้ว เพราะใช้ bottom nav แทน */}
      {/* คง code ไว้เพื่อ rollback ง่ายถ้าต้องการ */}

      {/* Sidebar — แสดงเฉพาะ desktop */}
      {!isMobile && (
        <aside style={{ width: 220, background: '#fff', color: '#1e293b', display: 'flex', flexDirection: 'column', padding: '24px 0',
          borderRight: '1px solid #e2e8f0', boxShadow: '2px 0 8px rgba(0,0,0,0.04)', overflowY: 'auto',
          position: 'relative', zIndex: 100 }}>
          <div style={{ padding: '0 24px 24px', borderBottom: '1px solid #e2e8f0' }}>
            <h1 style={{ fontSize: 18, fontWeight: 700, color: '#0ea5e9' }}>💬 {_brand}</h1>
            <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{tenant.name || 'Multi-channel AI'}</p>
          </div>
          <nav style={{ flex: 1, padding: '16px 0' }}>

            {/* Dashboard */}
            <NavLink to="/" end style={({ isActive }) => navStyle(isActive)}>
              <LayoutDashboard size={18} />Dashboard
            </NavLink>

            {/* Inbox with sub-channels */}
            <div>
              <div onClick={() => setInboxOpen(!inboxOpen)}
                style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 24px', color:'#64748b', cursor:'pointer', fontSize:14,
                  borderRight:'3px solid transparent', userSelect:'none' }}
                onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                <MessageSquare size={18} />
                <span style={{ flex:1 }}>Inbox</span>
                {convCounts['_total'] > 0 && (
                  <span style={{ background:'#ef4444', color:'#fff', borderRadius:10, padding:'1px 7px', fontSize:11, fontWeight:700, minWidth:18, textAlign:'center' }}>
                    {convCounts['_total']}
                  </span>
                )}
                {inboxOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </div>

              {inboxOpen && (
                <div style={{ background:'#f8fafc', borderBottom:'1px solid #f1f5f9' }}>
                  {/* ทั้งหมด */}
                  <NavLink to="/inbox" end style={() => ({
                    display:'flex', alignItems:'center', gap:8, padding:'8px 24px 8px 36px',
                    color: isInboxAll ? '#0ea5e9' : '#64748b', textDecoration:'none', fontSize:13,
                    background: isInboxAll ? '#e0f2fe' : 'transparent',
                    borderRight: isInboxAll ? '3px solid #0ea5e9' : '3px solid transparent',
                    fontWeight: isInboxAll ? 600 : 400,
                  })}>
                    <span>📥</span>
                    <span style={{ flex:1 }}>ทั้งหมด</span>
                    {convCounts['_total'] > 0 && (
                      <span style={{ background:'#ef4444', color:'#fff', borderRadius:10, padding:'1px 6px', fontSize:10, fontWeight:700 }}>
                        {convCounts['_total']}
                      </span>
                    )}
                  </NavLink>

                  {/* จัดกลุ่มตาม tag */}
                  {(() => {
                    const tagged = channels.filter(c => c.tag);
                    const untagged = channels.filter(c => !c.tag);
                    const groups = [...new Set(tagged.map(c => c.tag))];
                    const renderChannel = (ch, indent = 36) => (
                      <NavLink key={ch.id} to={`/inbox?channel=${ch.id}`}
                        style={() => {
                          const chActive = location.pathname === '/inbox' && currentChannel === ch.id;
                          return ({
                            display:'flex', alignItems:'center', gap:8, padding:`8px 24px 8px ${indent}px`,
                            color: chActive ? '#0ea5e9' : '#64748b', textDecoration:'none', fontSize:13,
                            background: chActive ? '#e0f2fe' : 'transparent',
                            borderRight: chActive ? '3px solid #0ea5e9' : '3px solid transparent',
                            fontWeight: chActive ? 600 : 400,
                          });
                        }}>
                        <span>{TYPE_ICONS[ch.type] || '📡'}</span>
                        <span style={{ flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{ch.name}</span>
                        {convCounts[ch.id] > 0 && (
                          <span style={{ background:'#ef4444', color:'#fff', borderRadius:10, padding:'1px 6px', fontSize:10, fontWeight:700 }}>
                            {convCounts[ch.id]}
                          </span>
                        )}
                      </NavLink>
                    );
                    return <>
                      {groups.map(tag => {
                        const groupChannels = channels.filter(c => c.tag === tag);
                        const groupCount = groupChannels.reduce((s,c) => s + (convCounts[c.id]||0), 0);
                        return (
                          <div key={tag}>
                            <div style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 24px 4px 36px', fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                              <span>📁</span><span style={{ flex:1 }}>{tag}</span>
                              {groupCount > 0 && <span style={{ background:'#f59e0b', color:'#fff', borderRadius:10, padding:'1px 6px', fontSize:10, fontWeight:700 }}>{groupCount}</span>}
                            </div>
                            {groupChannels.map(ch => renderChannel(ch, 44))}
                          </div>
                        );
                      })}
                      {untagged.map(ch => renderChannel(ch, 36))}
                    </>;
                  })()}
                </div>
              )}
            </div>

            {/* เมนูที่เหลือ */}
            {nav.filter(({ to }) => {
              if (to === '/channels') return canSee('channels');
              if (to === '/agents') return canSee('agents');
              if (to === '/quick-replies') return canSee('quickreplies');
              if (to === '/broadcast') return (!isAgent || perms.broadcast === true) && features.hasChat; // broadcast_v1 broadcast_perm_v1
              if (to === '/team') return features.hasChat; // team_status_v1 team_merge_v1 (owner=live+time / agent=time)
              if (to === '/ai') return canSee('ai_assistant') && features.hasChat; // ai_perm_v1
              if (to === '/live-selling') return features.hasCF === true; // cf_engine_v1
              // feature_flags_v1: chat/bill gating
              const CHAT_ROUTES = ['/inbox', '/channels', '/agents', '/shifts', '/reports/time', '/team', '/bot-training', '/telegram-settings', '/hot-games', '/quick-replies'];
              const BILL_ROUTES = ['/invoices', '/products', '/customers'];
              if (CHAT_ROUTES.includes(to) && !features.hasChat) return false;
              if (BILL_ROUTES.includes(to) && !features.hasBill) return false;
              if (BILL_ROUTES.includes(to) && features.hasBill) return false; /* bill_menu_hide_v1: bill enabled -> use bill UI */
              if (to === '/kpi') return canSee('kpi');
              if (to === '/reports/time') return canSee('time_report');
              if (to === '/shifts') return true; // shifts-v1: ทุกคนเห็น
              if (to === '/bot-training') return canSee('kb');
              if (to === '/settings') return canSee('settings');
              if (to === '/telegram-settings') return canSee('telegram');
              if (to === '/hot-games') return canSee('hot_games');
              if (to === '/invoices') return canSee('invoices'); // invoice_frontend_v1
              if (to === '/products') return canSee('products'); // invoice_frontend_v1
              if (to === '/customers') return canSee('customers'); // customer_list_v1
              return true;
            }).map(({ to, icon: Icon, label }) => (
              <NavLink key={to} to={to} end={to === '/'}
                style={({ isActive }) => navStyle(isActive)}>
                <Icon size={18} />{label}
              </NavLink>
            ))}
            {/* bill_sso_v1: ระบบบิล (owner + hasBill) -> jumps to the bill app */}
            {features.billAccess && (
              <button onClick={openBillSystem}
                style={{ ...navStyle(false), border: 'none', background: 'transparent', cursor: 'pointer', width: '100%', textAlign: 'left', font: 'inherit' }}>
                <FileText size={18} />ระบบบิล
                <span style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8' }}>↗</span>
              </button>
            )}
            {/* bill_trial_v1: owner without bill -> free 3-day trial */}
            {!features.billAccess && features.billTrialAvailable && (
              <button onClick={startBillTrial}
                style={{ ...navStyle(false), border: 'none', background: 'transparent', cursor: 'pointer', width: '100%', textAlign: 'left', font: 'inherit' }}>
                <FileText size={18} />ทดลองระบบบิล
                <span style={{ marginLeft: 'auto', fontSize: 10, background: '#f59e0b', color: '#fff', borderRadius: 6, padding: '1px 7px', fontWeight: 700 }}>ฟรี 3 วัน</span>
              </button>
            )}
            {/* staff_chat_sidebar_v1: staff chat entry */}
            <button onClick={() => window.dispatchEvent(new Event('toggle-staff-chat'))}
              style={{ ...navStyle(false), border: 'none', background: 'transparent', cursor: 'pointer', width: '100%', textAlign: 'left', font: 'inherit' }}>
              <MessageSquare size={18} />แชทพนักงาน
              {staffUnread > 0 && <span style={{ marginLeft: 'auto', background: '#ef4444', color: '#fff', borderRadius: 10, minWidth: 18, height: 18, fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px' }}>{staffUnread > 99 ? '99+' : staffUnread}</span>}
            </button>
            {/* theme_dark_v1: light/dark toggle */}
            <button onClick={() => { const n = !dark; setDark(n); try { localStorage.setItem('theme', n ? 'dark' : 'light'); } catch (e) {} document.documentElement.setAttribute('data-theme', n ? 'dark' : 'light'); }}
              style={{ ...navStyle(false), border: 'none', background: 'transparent', cursor: 'pointer', width: '100%', textAlign: 'left', font: 'inherit' }}>
              {dark ? <Sun size={18} /> : <Moon size={18} />}{dark ? 'โหมดสว่าง' : 'โหมดมืด'}
            </button>
          </nav>
          {/* Online Users Panel */}
          {onlineUsers.length > 0 && (
            <div style={{ padding:'12px 16px', borderTop:'1px solid #f1f5f9', borderBottom:'1px solid #f1f5f9', marginBottom:8 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#64748b', marginBottom:8, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                🟢 ออนไลน์ ({onlineUsers.length})
              </div>
              {onlineUsers.map(u => (
                <div key={u.user_id} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                  <div style={{ width:8, height:8, borderRadius:'50%', background:'#22c55e', flexShrink:0 }} />
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:12, fontWeight:600, color:'#1e293b', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {u.user_name || 'Unknown'}
                    </div>
                    <div style={{ fontSize:10, color:'#94a3b8', display:'flex', gap:6 }}>
                      <span>📍{u.current_page}</span>
                      {u.cases_count > 0 && <span>📋{u.cases_count}เคส</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div style={{ padding: '10px 16px', borderTop: '1px solid #f1f5f9' }}>
            <ClockButton />
          </div>
          <button onClick={logout} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 24px', background:'none', border:'none', color:'#ef4444', cursor:'pointer', fontSize:14 }}>
            <LogOut size={18} />ออกจากระบบ
          </button>
        </aside>
      )}

      {/* Main content */}
      <main style={{
        flex:1, overflow:'auto',
        // mobile_v2: ใช้ --app-vh แทน 100vh — แก้ iOS Safari dynamic toolbar ทำให้ 100vh ใหญ่กว่าจอจริง
        height: isMobile ? 'calc(var(--app-vh, 1vh) * 100 - 64px)' : 'auto',
        padding: isMobile ? 0 : 24,
        background:'#f8fafc'
      }}>
        <><ShiftReminderBanner /><Outlet /></>
      </main>

      {/* mobile_v1: Bottom Navigation Bar */}
      {isMobile && (
        <nav style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          background: '#fff', borderTop: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'space-around', alignItems: 'stretch',
          height: 64, zIndex: 90,
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',  // iPhone notch support
          boxShadow: '0 -2px 8px rgba(0,0,0,0.04)',
        }}>
          {/* feature_flags_v1_mobile: hide chat button when chat disabled */}
          {features.hasChat && <BottomNavBtn
            icon={MessageSquare} label="แชท"
            active={isInboxRoute}
            onClick={() => navigate('/inbox')}
            badge={convCounts['_total'] || 0}
          />}
          <BottomNavBtn
            icon={BarChart2} label="รายงาน"
            active={isReportRoute && !moreOpen && !profileOpen}
            onClick={() => navigate(canSee('kpi') ? '/kpi' : '/')}
          />
          <BottomNavBtn
            icon={Plus} label="เพิ่มเติม"
            active={moreOpen}
            onClick={() => { setMoreOpen(!moreOpen); setProfileOpen(false); }}
          />
          <BottomNavBtn
            icon={User} label="ฉัน"
            active={profileOpen}
            onClick={() => { setProfileOpen(!profileOpen); setMoreOpen(false); }}
          />
        </nav>
      )}

      {/* mobile_v1: "เพิ่มเติม" Drawer */}
      {isMobile && moreOpen && (
        <>
          <div onClick={() => setMoreOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 95 }} />
          <div style={{
            position: 'fixed', bottom: 64, left: 0, right: 0,
            background: '#fff', borderTopLeftRadius: 16, borderTopRightRadius: 16,
            padding: '20px 16px', zIndex: 96,
            maxHeight: '70vh', overflowY: 'auto',
            boxShadow: '0 -4px 16px rgba(0,0,0,0.1)',
          }}>
            <div style={{ width: 40, height: 4, background: '#e2e8f0', borderRadius: 2, margin: '0 auto 16px' }} />
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', margin: '0 0 16px', padding: '0 4px' }}>เมนูเพิ่มเติม</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              {/* feature_flags_v1_mobile: gate chat/bill routes in drawer */}
              {moreMenuItems.filter(item => {
                /* mobile_broadcast_fix_v1: เพิ่ม /broadcast — เดสก์ท็อปก็ต้องมี features.hasChat เหมือนกัน */
                const CHAT_ROUTES = ['/inbox', '/channels', '/agents', '/shifts', '/reports/time', '/team', '/bot-training', '/telegram-settings', '/hot-games', '/quick-replies', '/broadcast'];
                const BILL_ROUTES = ['/invoices', '/products', '/customers'];
                if (CHAT_ROUTES.includes(item.to) && !features.hasChat) return false;
                if (BILL_ROUTES.includes(item.to) && !features.hasBill) return false;
                if (BILL_ROUTES.includes(item.to) && features.hasBill) return false; /* bill_menu_hide_v1 */
                if (item.to === '/live-selling') return features.hasCF === true; /* cf_engine_v1 */
                return canSee(item.perm);
              }).map(item => (
                <button key={item.to}
                  onClick={() => { setMoreOpen(false); navigate(item.to); }}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                    padding: '14px 8px', background: '#f8fafc', border: '1px solid #e2e8f0',
                    borderRadius: 12, cursor: 'pointer',
                  }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 10,
                    background: '#FCEACA', color: '#854F0B',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <item.icon size={20} />
                  </div>
                  <span style={{ fontSize: 11, color: '#475569', fontWeight: 500, textAlign: 'center' }}>{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* mobile_v1: "ฉัน" Drawer (Profile) */}
      {isMobile && profileOpen && (
        <>
          <div onClick={() => setProfileOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 95 }} />
          <div style={{
            position: 'fixed', bottom: 64, left: 0, right: 0,
            background: '#fff', borderTopLeftRadius: 16, borderTopRightRadius: 16,
            padding: '20px 16px 16px', zIndex: 96,
            maxHeight: '70vh', overflowY: 'auto',
            boxShadow: '0 -4px 16px rgba(0,0,0,0.1)',
          }}>
            <div style={{ width: 40, height: 4, background: '#e2e8f0', borderRadius: 2, margin: '0 auto 16px' }} />

            {/* Profile header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px 4px 16px', borderBottom: '1px solid #f1f5f9', marginBottom: 12 }}>
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                background: '#FCEACA', color: '#854F0B',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22, fontWeight: 700,
              }}>
                {(tenant.name || tenant.email || 'U')[0].toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {tenant.name || 'ผู้ใช้งาน'}
                </div>
                <div style={{ fontSize: 12, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {tenant.email || tenant.role || ''}
                </div>
                {tenant.role && (
                  <span style={{
                    display: 'inline-block', marginTop: 4,
                    background: '#FCEACA', color: '#854F0B',
                    fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 8,
                    textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}>{tenant.role}</span>
                )}
              </div>
            </div>

            {/* Online users (if any) */}
            {onlineUsers.length > 0 && (
              <div style={{ padding: '8px 4px 12px', borderBottom: '1px solid #f1f5f9', marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  🟢 ออนไลน์ ({onlineUsers.length})
                </div>
                <div style={{ maxHeight: 120, overflowY: 'auto' }}>
                  {onlineUsers.map(u => (
                    <div key={u.user_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {u.user_name || 'Unknown'}
                        </div>
                        <div style={{ fontSize: 10, color: '#94a3b8' }}>
                          📍{u.current_page} {u.cases_count > 0 && `• 📋${u.cases_count}เคส`}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Clock in/out */}
            <div style={{ marginBottom: 12 }}>
              <ClockButton />
            </div>

            {/* Logout button */}
            <button onClick={logout}
              style={{
                width: '100%', padding: '14px',
                background: '#fef2f2', color: '#dc2626',
                border: '1px solid #fecaca', borderRadius: 12,
                fontSize: 15, fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}>
              <LogOut size={18} />ออกจากระบบ
            </button>
          </div>
        </>
      )}
    </div>
    <StaffChatWidget /> {/* staff_chat_v1 widget */}
  </>
  );
}
